/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Symmetry is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Symmetry is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Symmetry, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Symmetry:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Leverages 'code splitting' for some features.
 *  - Is completely custom built (no JS libraries)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Symmetry broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
*   4.  on:cart:after-merge
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - selectedOptions: an array of currently selected options (e.g. ['Blue', 'Large'])
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:after-merge
 * -------------------------------------------------------------------------------------------------
 * Fires after a list of cart items has finished being dynamically updated after a cart change.
 *
 * How to listen:
 * document.addEventListener('on:cart:after-merge', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Symmetry utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Symmetry has no third-party JavaScript dependencies.
 *
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */


// ── Mobile drawer top offset: distance from viewport top to bottom of full header ──
// .section-header sits BELOW the announcement bar in document flow, so its
// getBoundingClientRect().bottom already includes the announcement bar height.
(function () {
  function measure() {
    var header = document.querySelector('.section-header');
    if (!header) return;
    var bottom = Math.ceil(header.getBoundingClientRect().bottom);
    if (bottom > 0) {
      document.documentElement.style.setProperty('--pb-header-bottom', bottom + 'px');
    }
  }
  // Measure after load (fonts/images settled) and on resize
  window.addEventListener('load', measure);
  window.addEventListener('resize', measure);
  // Re-measure in capture phase just before hamburger click opens the nav
  document.addEventListener('click', function (e) {
    if (e.target.closest('.mobile-nav-toggle')) measure();
  }, true);
  measure();
})();

// ── Mobile drawer footer: inject Login + currency ──
(function () {
  var userSVG = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>';

  function injectFooter(drawer) {
    if (drawer.dataset.pbFooterInjected) return;
    drawer.dataset.pbFooterInjected = '1';

    var footer = drawer.querySelector('.mobile-navigation-drawer__footer');
    if (!footer) return;

    var loginHref = (window.routes && window.routes.account_login_url) ? window.routes.account_login_url : '/account/login';
    var acct = document.querySelector('.header-account-link');
    if (acct && acct.href) loginHref = acct.href;

    var div = document.createElement('div');
    div.className = 'pb-mob-footer';
    div.innerHTML = '<a href="' + loginHref + '" class="pb-mob-footer__login">' + userSVG + ' Login</a>';

    var locGrid = document.querySelector('.section-footer .localization__grid');
    if (locGrid) {
      var clone = locGrid.cloneNode(true);
      var sels = clone.querySelectorAll('.localization__selector');
      if (sels.length > 1) sels[sels.length - 1].remove();
      div.appendChild(clone);
    }

    footer.appendChild(div);
  }

  var observer = new MutationObserver(function (mutations) {
    for (var i = 0; i < mutations.length; i++) {
      var added = mutations[i].addedNodes;
      for (var j = 0; j < added.length; j++) {
        var node = added[j];
        if (node.nodeType !== 1) continue;
        var drawer = node.classList && node.classList.contains('mobile-navigation-drawer')
          ? node : node.querySelector && node.querySelector('.mobile-navigation-drawer');
        if (drawer) injectFooter(drawer);
      }
    }
  });

  observer.observe(document.body, { childList: true, subtree: true });
})();

// ── Mobile product page — fade carousel (no horizontal slide motion) ──
(function () {
  function initFadeCarousel(carousel) {
    if (carousel.dataset.pbFade) return;

    var slider = carousel.querySelector('.slider');
    var rawSlides = Array.prototype.slice.call(carousel.querySelectorAll('.slider__item'));
    // Bail BEFORE marking as initialised — otherwise the CSS (gated on
    // [data-pb-fade]) would hide every slide while no pb-fade-active is set.
    if (rawSlides.length < 1 || !slider) return;

    carousel.dataset.pbFade = '1';

    // The carousel clones slides for its loop, so the same media id appears
    // multiple times in the DOM. Dedupe to the FIRST occurrence of each media id,
    // preserving DOM order (= product.media order). Everything below works on
    // these unique slides so swiping can't land on a hidden clone.
    var slides = [];
    var seen = {};
    function slideId(el) { return el.getAttribute('data-media-id'); }
    rawSlides.forEach(function (s) {
      var id = slideId(s);
      if (id && !seen[id]) { seen[id] = true; slides.push(s); }
    });

    // Map each slide's media id → its color group (from the theme's grouping JSON),
    // so swiping stays within the selected variant's own images (front/back of THAT
    // color) instead of drifting into another color's photos. "all-groups" media
    // belong to every group.
    var mediaGroup = {}; // mediaId(str) → group name, or '*' for all-groups
    (function () {
      var gallery = carousel.closest('media-gallery');
      var json = gallery && gallery.querySelector('.js-data-variant-media');
      if (!json) return;
      try {
        var data = JSON.parse(json.textContent).media || {};
        Object.keys(data).forEach(function (id) {
          var g = data[id];
          mediaGroup[id] = (g && g.group === true) ? '*' : (g && g.group) || null;
        });
      } catch (err) { /* no grouping → global swipe fallback */ }
    })();

    // Unique slides that belong to a given group (plus any all-groups media). If we
    // have no grouping data, fall back to the first two slides.
    function groupSlides(group) {
      if (!group) return slides.slice(0, 2);
      return slides.filter(function (s) {
        var g = mediaGroup[slideId(s)];
        return g === group || g === '*';
      });
    }

    var currentGroup = mediaGroup[slideId(slides[0])] || null;
    var swipeSlides = groupSlides(currentGroup);

    var activeEl = slides[0];
    activeEl.classList.add('pb-fade-active');

    // Resolve any (possibly cloned) slide to the unique first-occurrence slide.
    function unique(el) {
      if (!el) return null;
      var id = slideId(el);
      for (var k = 0; k < slides.length; k++) {
        if (slideId(slides[k]) === id) return slides[k];
      }
      return el;
    }

    function show(el) {
      el = unique(el);
      if (!el || el === activeEl) return;
      el.removeAttribute('hidden');
      if (activeEl) activeEl.classList.remove('pb-fade-active');
      el.classList.add('pb-fade-active');
      activeEl = el;
      // Silently sync theme's internal scrollLeft so dot/arrow nav still works
      var idx = rawSlides.indexOf(el);
      slider.style.scrollBehavior = 'auto';
      var span = carousel.slideSpan || slider.clientWidth;
      slider.scrollLeft = span * Math.max(0, idx);
      requestAnimationFrame(function () { slider.style.scrollBehavior = ''; });
    }

    // Swipe detection — cycles within the active variant's group (front/back)
    var touchStartX = null;
    carousel.addEventListener('touchstart', function (e) {
      touchStartX = e.touches[0].clientX;
    }, { passive: true });

    carousel.addEventListener('touchend', function (e) {
      if (touchStartX === null) return;
      var dx = e.changedTouches[0].clientX - touchStartX;
      touchStartX = null;
      if (Math.abs(dx) < 30 || swipeSlides.length < 2) return;
      var i = swipeSlides.indexOf(activeEl);
      if (i === -1) i = 0;
      var next = dx < 0 ? i + 1 : i - 1;
      if (next >= 0 && next < swipeSlides.length) show(swipeSlides[next]);
    }, { passive: true });

    // Sync when theme's dots/arrows change the index
    carousel.addEventListener('on:carousel-slider:select', function (e) {
      var idx = e.detail && e.detail.index;
      if (typeof idx === 'number' && rawSlides[idx]) show(rawSlides[idx]);
    });

    // ── Variant change → fade to that variant's featured image ──
    // Swatch taps on mobile change the variant but the theme can't scroll this
    // locked slider; fade to the matching slide by media id and re-scope swipe to
    // that variant's color group so back/front stay the same color.
    document.addEventListener('on:variant:change', function (e) {
      if (window.innerWidth > 767) return;
      var v = e.detail && e.detail.variant;
      if (!v || !v.featured_media) return;
      var target = carousel.querySelector('.slider__item[data-media-id="' + v.featured_media.id + '"]');
      if (!target) return;
      currentGroup = mediaGroup[slideId(target)] || currentGroup;
      swipeSlides = groupSlides(currentGroup);
      show(target);
    });
  }

  function tryInit() {
    if (window.innerWidth > 767) return;
    var carousels = document.querySelectorAll(
      '.section-main-product .main-image carousel-slider'
    );
    carousels.forEach(initFadeCarousel);
  }

  // Run after theme has initialised carousels
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { setTimeout(tryInit, 300); });
  } else {
    setTimeout(tryInit, 300);
  }

  // Re-run when the viewport crosses into mobile. The initial tryInit bails on
  // desktop, so without this a desktop→mobile switch (responsive toggle / rotate)
  // would leave the carousel uninitialised. initFadeCarousel is idempotent
  // (guards on data-pb-fade), so calling repeatedly is safe.
  document.addEventListener('on:breakpoint-change', tryInit);
  var pbFadeResizeTimer;
  window.addEventListener('resize', function () {
    clearTimeout(pbFadeResizeTimer);
    pbFadeResizeTimer = setTimeout(tryInit, 200);
  });

})();


// ── Product page — collapse blank description blocks ──
// Some products have a Description left as whitespace-only markup (e.g.
// "<p>&nbsp;</p><div><br></div>"). The theme only hides a truly :empty
// description, so these render as a big empty gap (content height + 50px
// top/bottom margins) between the price and the Color selector. Hide any
// product-description that has no real text so the gap disappears.
(function () {
  function hideBlankDescriptions() {
    var blocks = document.querySelectorAll('.section-main-product .product-description');
    blocks.forEach(function (el) {
      // Strip non-breaking spaces and whitespace; if nothing meaningful and no
      // media (images/iframes), it's an empty description → hide it.
      var text = (el.textContent || '').replace(/\u00a0/g, '').trim();
      var hasMedia = el.querySelector('img, iframe, video, table, ul, ol');
      if (!text && !hasMedia) {
        el.style.setProperty('display', 'none', 'important');
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', hideBlankDescriptions);
  } else {
    hideBlankDescriptions();
  }
})();


// ── Mobile search — inject popular searches + browse categories ──
(function () {
  var POPULAR = ['Tees', 'Hoodies', 'Denim', 'Shorts', 'New Arrivals'];
  var CATS = [
    { label: 'New Arrivals', href: '/collections/new' },
    { label: 'Tees',         href: '/collections/graphic-tees' },
    { label: 'Hoodies',      href: '/collections/hoodies' },
    { label: 'Bottoms',      href: '/collections/bottoms' },
    { label: 'Best Sellers', href: '/collections/best-sellers-dameapparel' },
  ];

  var CHEVRON = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>';

  function buildPopular() {
    var wrap = document.createElement('div');
    wrap.className = 'pb-search-popular';

    var html = '<p class="pb-search-label">POPULAR SEARCHES</p><div class="pb-search-pills">';
    POPULAR.forEach(function (t) {
      html += '<a href="/search?q=' + encodeURIComponent(t) + '" class="pb-search-pill">' + t + '</a>';
    });
    html += '</div>';

    html += '<p class="pb-search-label">BROWSE</p><div class="pb-search-cats">';
    CATS.forEach(function (c) {
      html += '<a href="' + c.href + '" class="pb-search-cat"><span>' + c.label + '</span>' + CHEVRON + '</a>';
    });
    html += '</div>';

    wrap.innerHTML = html;
    return wrap;
  }

  function injectIfMobile() {
    if (window.innerWidth > 767) return;
    var container = document.querySelector('.section-header .main-search__container');
    if (!container || container.querySelector('.pb-search-popular')) return;
    var form = container.querySelector('.main-search__form');
    if (form) form.insertAdjacentElement('afterend', buildPopular());
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', injectIfMobile);
  } else {
    injectIfMobile();
  }
})();

// ── Footer accessibility link → open Accessibility Assistant widget ──
(function () {
  document.addEventListener('click', function (e) {
    var link = e.target.closest('a[href="/pages/accessibility"]');
    if (!link) return;
    // Only intercept links outside the main nav (nav dropdown handled by the app itself)
    if (link.closest('.navigation')) return;
    e.preventDefault();
    // Try common Accessibility Assistant toggle button selectors
    var btn = document.querySelector(
      '[class*="aa-cc-toggle"], [class*="aa-mm-open"], [class*="aa-cc-open"], ' +
      '[id*="accessibility-toggle"], [data-aa-toggle], ' +
      'button[class*="aa-"], div[class*="aa-cc-btn"]'
    );
    if (btn) {
      btn.click();
    } else {
      // Fallback: navigate to the page if widget button not found
      window.location.href = '/pages/accessibility';
    }
  });
})();

// ── Collection filter toggle safety net ──
(function () {
  // NOTE: product-card colour-swatch behaviour (click-to-select colour,
  // persistent image swap, and driving size pills / quick-add / product links)
  // is handled by the delegated script in layout/theme.liquid. It intentionally
  // lives there so all swatch logic is in one place and does not conflict.

  // ── Filter toggle safety net ──
  // Records state before the click, then checks after all handlers have run
  // (setTimeout 0). If the theme's own delegated handler didn't toggle the
  // filter-container classes, we do it here as a fallback.
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-toggle-filters]');
    if (!btn) return;
    e.preventDefault();
    var fc = document.querySelector('filter-container');
    if (!fc) return;
    var stateBefore = fc.classList.contains('filter-container--show-filters-desktop');
    setTimeout(function () {
      // If state unchanged, theme handler didn't fire — toggle it ourselves
      if (fc.classList.contains('filter-container--show-filters-desktop') === stateBefore) {
        fc.classList.toggle('filter-container--show-filters-desktop');
        fc.classList.toggle('filter-container--show-filters-mobile');
        var visible = fc.classList.contains('filter-container--show-filters-desktop');
        document.querySelectorAll('.toggle-btn[data-toggle-filters]').forEach(function (b) {
          b.classList.toggle('toggle-btn--revealed-desktop', visible);
        });
      }
    }, 0);
  });

})();

// ── Collection filter button: never overlap the footer ──
// The floating filter pill is position:fixed (bottom:100px), so on a full scroll
// it sits on top of the footer. Watch scroll/resize and, once the footer enters
// the viewport, raise the button so its base stays a fixed gap above the footer.
(function () {
  var BASE_BOTTOM = 60; // must match custom.css .toggle-btn[data-toggle-filters] bottom
  var GAP = 60;          // clearance between button and footer top (matches left padding)

  var btn = document.querySelector('.toggle-btn[data-toggle-filters]');
  if (!btn) return;

  var footer = document.querySelector('.shopify-section-group-footer-group')
            || document.querySelector('.section-footer__row-container')
            || document.querySelector('footer');
  if (!footer) return;

  var ticking = false;

  function update() {
    ticking = false;
    // Only relevant where the button floats (desktop). Otherwise clear any override.
    if (!window.matchMedia('(min-width: 768px)').matches) {
      btn.style.removeProperty('bottom');
      return;
    }
    var footerTop = footer.getBoundingClientRect().top;
    var overlap = window.innerHeight - footerTop; // px of footer visible from bottom
    var bottom = overlap > (BASE_BOTTOM - GAP) ? (overlap + GAP) : BASE_BOTTOM;
    // Inline !important is required to beat the stylesheet's !important bottom.
    btn.style.setProperty('bottom', bottom + 'px', 'important');
  }

  function onScroll() {
    if (!ticking) {
      ticking = true;
      window.requestAnimationFrame(update);
    }
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll);
  update();
})();

// ── Cart drawer — click-to-toggle + swipe-to-dismiss (mobile) ──
(function () {
  function getDrawer() { return document.querySelector('.js-cart-drawer'); }
  function isOpen(d) { return d && d.hasAttribute('open'); }

  // 1) Toggle: tapping the header cart button while the drawer is OPEN closes it.
  // The theme binds a bubble-phase delegated handler on the header that always
  // calls .open(). We intercept in the CAPTURE phase (runs first); if the drawer
  // is already open we close it and stop the event so the theme can't re-open it.
  document.addEventListener('click', function (e) {
    var link = e.target.closest && e.target.closest('.cart-link');
    if (!link) return;
    var drawer = getDrawer();
    if (isOpen(drawer)) {
      e.preventDefault();
      e.stopPropagation();
      drawer.close();
    }
  }, true);

  // 2) Swipe-to-dismiss on mobile. The drawer is anchored to the right, so a
  // rightward swipe dismisses it. Only act on a predominantly-horizontal swipe
  // so vertical scrolling of the cart item list is never hijacked.
  var startX = null, startY = null, tracking = false;

  document.addEventListener('touchstart', function (e) {
    var drawer = getDrawer();
    if (!isOpen(drawer) || window.innerWidth > 767) { tracking = false; return; }
    startX = e.touches[0].clientX;
    startY = e.touches[0].clientY;
    tracking = true;
  }, { passive: true });

  document.addEventListener('touchend', function (e) {
    if (!tracking) return;
    tracking = false;
    var dx = e.changedTouches[0].clientX - startX;
    var dy = e.changedTouches[0].clientY - startY;
    // Rightward, clearly horizontal, and past a comfortable threshold.
    if (dx > 70 && Math.abs(dx) > Math.abs(dy) * 1.5) {
      var drawer = getDrawer();
      if (isOpen(drawer)) drawer.close();
    }
  }, { passive: true });
})();

// ── Cart drawer & mobile nav are mutually exclusive ──
// The theme gives both the cart drawer and the mobile-nav drawer z-index 8001,
// so when both are open they stack on top of each other (DOM-order dependent)
// and you can't see the one underneath. Enforce "only one open at a time":
// opening the mobile nav closes the cart, and opening the cart closes the nav.
(function () {
  function cartDrawer() { return document.querySelector('.js-cart-drawer'); }
  function cartOpen() { var d = cartDrawer(); return !!(d && d.hasAttribute('open')); }
  function navOpen() { return document.body.classList.contains('reveal-mobile-nav'); }

  function closeCart() {
    var d = cartDrawer();
    if (d && cartOpen() && typeof d.close === 'function') d.close();
  }
  function closeNav() {
    if (!navOpen()) return;
    // Re-use the theme's own toggle so focus-trap teardown/transitions run.
    // With the nav open, a .mobile-nav-toggle click closes it.
    var toggle = document.querySelector('.mobile-nav-toggle');
    if (toggle) { toggle.click(); return; }
    document.body.classList.remove('reveal-mobile-nav', 'reveal-mobile-nav--revealed');
    setTimeout(function () {
      document.body.classList.remove('enable-mobile-nav-transition');
    }, 750);
  }

  // Nav opened (body gains reveal-mobile-nav) → close the cart.
  var observer = new MutationObserver(function () {
    if (navOpen() && cartOpen()) closeCart();
  });
  observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });

  // Cart about to open → close the nav first.
  document.addEventListener('on:cart-drawer:before-open', closeNav);
})();

/* ── Mobile product image "1 / N" slide counter ──
   The main product gallery is a horizontal scroll-snap slider (.slider). We show
   a small counter overlay on mobile so shoppers know they can swipe through the
   images. Index is derived from scrollLeft / clientWidth (deterministic). */
(function () {
  function initSlideCounter() {
    var main = document.querySelector('.media-gallery .main-image');
    if (!main) return;

    // The scroll-snap track. Fall back gracefully if markup differs.
    var slider = main.querySelector('.slider');
    var grid = main.querySelector('.slider__grid');
    if (!slider || !grid) return;
    var items = grid.querySelectorAll('.slider__item');
    if (items.length < 2) return;

    var counter = main.querySelector('.pb-slide-counter');
    if (!counter) {
      counter = document.createElement('div');
      counter.className = 'pb-slide-counter';
      counter.innerHTML =
        '<button type="button" class="pb-slide-counter__arrow" data-dir="prev" aria-label="Previous image">\u2039</button>' +
        '<span class="pb-slide-counter__text"></span>' +
        '<button type="button" class="pb-slide-counter__arrow" data-dir="next" aria-label="Next image">\u203A</button>';
      main.appendChild(counter);
    }

    // Wire interactions once per counter element.
    if (!counter.dataset.wired) {
      counter.dataset.wired = '1';
      counter.addEventListener('click', function (e) {
        var btn = e.target.closest('.pb-slide-counter__arrow');
        if (!btn) return;
        var w = slider.clientWidth || 1;
        var current = Math.round(slider.scrollLeft / w);
        var next = btn.dataset.dir === 'next' ? current + 1 : current - 1;
        next = Math.max(0, Math.min(items.length - 1, next));
        slider.scrollTo({ left: next * w, behavior: 'smooth' });
      });
      slider.addEventListener('scroll', function () {
        window.requestAnimationFrame(update);
      }, { passive: true });
      window.addEventListener('resize', update);
    }

    var text = counter.querySelector('.pb-slide-counter__text');
    function update() {
      var w = slider.clientWidth || 1;
      var idx = Math.round(slider.scrollLeft / w) + 1;
      if (idx < 1) idx = 1;
      if (idx > items.length) idx = items.length;
      text.textContent = idx + ' / ' + items.length;
    }
    update();
  }

  function boot() {
    initSlideCounter();
    // The media gallery can be re-rendered dynamically (variant change / hydrate),
    // which wipes the appended counter — re-add it whenever the gallery mutates.
    var gallery = document.querySelector('.media-gallery');
    if (gallery && !gallery.dataset.counterObserved) {
      gallery.dataset.counterObserved = '1';
      var obs = new MutationObserver(function () {
        if (!gallery.querySelector('.pb-slide-counter')) initSlideCounter();
      });
      obs.observe(gallery, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  window.addEventListener('load', initSlideCounter);
  document.addEventListener('on:variant:change', initSlideCounter);
})();

